<template>
  <Dialog
    v-model:visible="visible"
    :header="title"
    modal
    class="p-fluid estudyoo-editor"
    :style="{ width: 'min(560px, 96vw)' }"
    @hide="onHide"
  >
    <template v-if="entity === 'actividad'">
      <div class="field mb-3">
        <label class="block text-sm mb-1">Título</label>
        <InputText v-model="form.titulo" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Subtítulo</label>
        <InputText v-model="form.subtitulo" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Instrucciones</label>
        <Textarea v-model="form.instrucciones" rows="3" class="w-full" auto-resize />
      </div>
      <div class="flex align-items-center gap-2">
        <CheckBox v-model="form.activa" input-id="ed-act-activa" :binary="true" />
        <label for="ed-act-activa" class="text-sm cursor-pointer">Activa</label>
      </div>
    </template>

    <template v-else-if="entity === 'capitulo'">
      <div class="field mb-3">
        <label class="block text-sm mb-1">Materia (vía eje)</label>
        <Dropdown
          v-model="form.eje_id"
          :options="ejeOpts"
          option-label="label"
          option-value="id"
          placeholder="Eje temático"
          class="w-full"
          filter
        />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Nivel curricular (curso)</label>
        <Dropdown
          v-model="form.nivel_simce"
          :options="nivelOpts"
          option-label="label"
          option-value="value"
          placeholder="Nivel"
          class="w-full"
          show-clear
        />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Nombre</label>
        <InputText v-model="form.nombre" class="w-full" />
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div class="field mb-3">
          <label class="block text-sm mb-1">Código</label>
          <InputText v-model="form.codigo" class="w-full" />
        </div>
        <div class="field mb-3">
          <label class="block text-sm mb-1">Orden</label>
          <InputNumber v-model="form.orden" :min="0" class="w-full" />
        </div>
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Descripción</label>
        <Textarea v-model="form.descripcion" rows="2" class="w-full" auto-resize />
      </div>
    </template>

    <template v-else-if="entity === 'materia'">
      <div class="field mb-3">
        <label class="block text-sm mb-1">Nombre</label>
        <InputText v-model="form.nombre" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Código</label>
        <InputText v-model="form.codigo" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Orden</label>
        <InputNumber v-model="form.orden" :min="0" class="w-full" />
      </div>
      <div class="flex align-items-center gap-2">
        <CheckBox v-model="form.activa" input-id="ed-mat-activa" :binary="true" />
        <label for="ed-mat-activa" class="text-sm cursor-pointer">Activa</label>
      </div>
    </template>

    <template v-else-if="entity === 'pregunta'">
      <p class="text-xs text-600 mb-3">
        Este diálogo solo cambia enunciado, tipo, explicación y orden.
        Para editar <strong>palabras, pares, fracción o alternativas</strong> de una actividad importada con IA,
        ve a la pestaña <strong>Actividades</strong> o <strong>Preguntas</strong> y usa
        <i class="pi pi-file-edit" /> <em>Editar actividad completa</em>.
      </p>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Enunciado</label>
        <Textarea v-model="form.enunciado" rows="3" class="w-full" auto-resize />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Tipo pregunta</label>
        <InputText v-model="form.tipo" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Explicación</label>
        <Textarea v-model="form.explicacion" rows="2" class="w-full" auto-resize />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Orden</label>
        <InputNumber v-model="form.orden" :min="0" class="w-full" />
      </div>
      <div class="flex align-items-center gap-2">
        <CheckBox v-model="form.activa" input-id="ed-preg-activa" :binary="true" />
        <label for="ed-preg-activa" class="text-sm cursor-pointer">Activa</label>
      </div>
    </template>

    <template v-else-if="entity === 'asignacion'">
      <div class="field mb-3">
        <label class="block text-sm mb-1">Curso</label>
        <Dropdown
          v-model="form.curso_id"
          :options="cursoOpts"
          option-label="label"
          option-value="id"
          class="w-full"
          filter
        />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Casa (opcional)</label>
        <Dropdown
          v-model="form.casa_id"
          :options="casaOptsNull"
          option-label="label"
          option-value="id"
          class="w-full"
          show-clear
        />
      </div>
      <div class="flex align-items-center gap-2">
        <CheckBox v-model="form.activa" input-id="ed-asig-activa" :binary="true" />
        <label for="ed-asig-activa" class="text-sm cursor-pointer">Asignación activa</label>
      </div>
    </template>

    <template v-else-if="entity === 'material_act' || entity === 'material_cap'">
      <div class="field mb-3">
        <label class="block text-sm mb-1">Título</label>
        <InputText v-model="form.titulo" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Tipo</label>
        <InputText v-model="form.tipo" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Contenido</label>
        <Textarea v-model="form.contenido" rows="3" class="w-full" auto-resize />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">URL</label>
        <InputText v-model="form.url" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Orden</label>
        <InputNumber v-model="form.orden" :min="0" class="w-full" />
      </div>
      <div v-if="entity === 'material_act'" class="flex align-items-center gap-2">
        <CheckBox v-model="form.obligatorio" input-id="ed-matob" :binary="true" />
        <label for="ed-matob" class="text-sm cursor-pointer">Obligatorio</label>
      </div>
    </template>

    <template v-else-if="entity === 'alternativa'">
      <div class="field mb-3">
        <label class="block text-sm mb-1">Texto</label>
        <InputText v-model="form.texto" class="w-full" />
      </div>
      <div class="field mb-3">
        <label class="block text-sm mb-1">Icono</label>
        <InputText v-model="form.icono" class="w-full" placeholder="help, circle, …" />
      </div>
      <div class="flex align-items-center gap-2">
        <CheckBox v-model="form.es_correcta" input-id="ed-alt-ok" :binary="true" />
        <label for="ed-alt-ok" class="text-sm cursor-pointer">Es correcta</label>
      </div>
    </template>

    <template #footer>
      <ButtonPrime label="Cancelar" class="p-button-text" @click="visible = false" />
      <ButtonPrime label="Guardar" icon="pi pi-check" :loading="saving" @click="guardar" />
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import CheckBox from 'primevue/checkbox';
import ButtonPrime from 'primevue/button';
import { ActividadesApi } from '@/services/EstudyooAdminApi';
import { NIVEL_OPTS } from '@/utils/nivelesEscolares';

const emit = defineEmits(['saved']);

const props = defineProps({
  ejes: { type: Array, default: () => [] },
  materias: { type: Array, default: () => [] },
  cursos: { type: Array, default: () => [] },
  casas: { type: Array, default: () => [] },
});

const visible = ref(false);
const saving = ref(false);
const entity = ref('');
const rowId = ref(null);
const form = ref({});

const nivelOpts = NIVEL_OPTS;

const titles = {
  actividad: 'Editar actividad',
  capitulo: 'Editar capítulo',
  materia: 'Editar materia',
  pregunta: 'Editar pregunta',
  asignacion: 'Editar asignación',
  material_act: 'Editar material de actividad',
  material_cap: 'Editar material de capítulo',
  alternativa: 'Editar alternativa',
};

const title = computed(() => titles[entity.value] || 'Editar');

const ejeOpts = computed(() =>
  (props.ejes || []).map((e) => {
    const mat = (props.materias || []).find((m) => m.id === e.materia_id);
    return {
      ...e,
      label: `${mat?.nombre || 'Materia'} → ${e.nombre || e.codigo}`,
    };
  }),
);

const cursoOpts = computed(() => props.cursos || []);

const casaOptsNull = computed(() => [
  { id: null, label: 'Todo el curso' },
  ...(props.casas || []).map((c) => ({
    id: c.id,
    label: `${c.codigo || c.nombre}${c.nombre && c.codigo ? ` — ${c.nombre}` : ''}`,
  })),
]);

function open(type, row) {
  entity.value = type;
  rowId.value = row.id;
  form.value = { ...row };
  if (type === 'asignacion') {
    form.value.casa_id = row.casa_id || null;
  }
  if (type === 'capitulo') {
    form.value.eje_id = row.eje_id || row.eje || null;
  }
  visible.value = true;
}

function onHide() {
  form.value = {};
  entity.value = '';
  rowId.value = null;
}

async function guardar() {
  if (!rowId.value || !entity.value) return;
  saving.value = true;
  try {
    let updated;
    const f = { ...form.value };
    delete f.id;
    delete f.creado_en;
    delete f.actualizado_en;

    switch (entity.value) {
      case 'actividad':
        updated = await ActividadesApi.patchActividad(rowId.value, {
          titulo: f.titulo,
          subtitulo: f.subtitulo,
          instrucciones: f.instrucciones,
          activa: f.activa,
        });
        break;
      case 'capitulo':
        updated = await ActividadesApi.patchCapitulo(rowId.value, {
          eje_id: f.eje_id,
          nivel_simce: f.nivel_simce || '',
          nombre: f.nombre,
          codigo: f.codigo,
          orden: f.orden,
          descripcion: f.descripcion,
        });
        break;
      case 'materia':
        updated = await ActividadesApi.patchMateria(rowId.value, {
          nombre: f.nombre,
          codigo: f.codigo,
          orden: f.orden,
          activa: f.activa,
        });
        break;
      case 'pregunta':
        updated = await ActividadesApi.patchPregunta(rowId.value, {
          enunciado: f.enunciado,
          tipo: f.tipo,
          explicacion: f.explicacion,
          orden: f.orden,
          activa: f.activa,
        });
        break;
      case 'asignacion':
        updated = await ActividadesApi.patchAsignacion(rowId.value, {
          curso_id: f.curso_id,
          casa_id: f.casa_id || null,
          activa: f.activa,
        });
        break;
      case 'material_act':
        updated = await ActividadesApi.patchMaterialActividad(rowId.value, {
          titulo: f.titulo,
          tipo: f.tipo,
          contenido: f.contenido,
          url: f.url,
          orden: f.orden,
          obligatorio: f.obligatorio,
        });
        break;
      case 'material_cap':
        updated = await ActividadesApi.patchMaterialCapitulo(rowId.value, {
          titulo: f.titulo,
          tipo: f.tipo,
          contenido: f.contenido,
          url: f.url,
          orden: f.orden,
        });
        break;
      case 'alternativa':
        updated = await ActividadesApi.patchAlternativa(rowId.value, {
          texto: f.texto,
          icono: f.icono,
          es_correcta: f.es_correcta,
        });
        break;
      default:
        return;
    }
    emit('saved', { entity: entity.value, row: updated });
    visible.value = false;
  } finally {
    saving.value = false;
  }
}

defineExpose({ open });
</script>
